'use client';

import { Placement } from '@hc-frontend/core-types';
import {
  Link,
  useEventBus,
  useExperiments,
  useTrackingContexts,
} from '@hc-frontend/core-ui';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import type { MouseEventHandler } from 'react';

import { useUccModalContext } from '../../organisms/universal-call-component/modal-provider';
import type { AdClickEvent, AdEventTarget } from '../ad-events-subscription';
import type { TypAdLinkClientProps } from './typ-ad-link.types';

const adClickCounts = new Map<string, number>();

export function TypAdLinkClient({
  productType,
  campaignPlacement,
  children,
  impressionPosition,
  impressionId,
  ad,
  sessionId,
  device,
  stateAcronym,
  zipCode,
  onClick,
  domainSessionId,
  domainUserId,
  userId,
  ...rest
}: TypAdLinkClientProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();
  const { showUccOnAdClickTreatment } = useExperiments();
  const ref = useUccModalContext();

  const updateAdClickCounts = () => {
    const value = adClickCounts.get(ad.id);
    adClickCounts.set(ad.id, value ? value + 1 : 1);
  };

  const handleAdClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.stopPropagation();
    updateAdClickCounts();
    onClick?.(e);

    if (showUccOnAdClickTreatment === 'variant') {
      ref?.current?.showModal();
    }

    eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
      id: 'click',
      topic: 'metric.client.message',
      source: e.currentTarget,
      metric: {
        name: 'click',
        tags: {
          action: 'Ad Click',
          platform: 'apollo',
          placement: campaignPlacement,
          product: productType,
          adSource: ad.adsSource ?? 'FeaturedAds',
        },
      },
    });

    eventBus.publish<AdClickEvent, AdEventTarget>({
      impressionId,
      impressionPosition,
      id: 'click',
      source: e.currentTarget,
      topic: 'core.ui.ad.click',
      featuredAd: {
        ...ad,
        adsSource: ad.adsSource ?? 'FeaturedAds',
      },
      placement: Placement.enum.TYP,
      sessionId,
      contexts,
      productType,
      device,
      stateAcronym,
      zipCode,
      clickCount: adClickCounts.get(ad.id) as number,
      domainUserId,
      domainSessionId,
      userId,
      clickPlatform: 'APOLLO',
      clickId: ad.uniqueClickId ?? '',
      clickTimestamp: new Date().toISOString(),
    });
  };

  return (
    <Link {...rest} onClick={handleAdClick}>
      {children}
    </Link>
  );
}
